import React from 'react';
import { motion } from 'framer-motion';
import { Bot, Megaphone, PlugZap, TrendingUp } from 'lucide-react';

const PILLARS = [
  {
    title: 'Atención por WhatsApp y voz',
    description: 'Respuestas, reservas, recordatorios, escalado humano y llamadas con IA cuando el canal escrito no basta.',
    icon: Bot,
    accent: '#22d3ee',
  },
  {
    title: 'Integraciones con tu software',
    description: 'Flowww, TIMP, Booksy, Treatwell, Calendar, CRM, email, hojas de cálculo y APIs: no sustituimos tu sistema, lo conectamos.',
    icon: PlugZap,
    accent: '#3CB371',
  },
  {
    title: 'Agentes de ventas',
    description: 'Captura, cualificación, seguimiento, pipeline y próximas acciones para que ningún interesado se quede olvidado.',
    icon: TrendingUp,
    accent: '#fb923c',
  },
  {
    title: 'Marketing online con IA',
    description: 'Instagram, Google Business, campañas WhatsApp, reseñas, reactivación y contenido conectado a tu operativa real.',
    icon: Megaphone,
    accent: '#a78bfa',
  },
];

export const PositioningPillarsSection: React.FC = () => {
  return (
    <section className="py-20 bg-white relative overflow-hidden">
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[900px] h-[420px] bg-emerald-400/5 rounded-full blur-[120px] pointer-events-none" />

      <div className="container mx-auto px-4 max-w-7xl relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center max-w-3xl mx-auto mb-12"
        >
          <span
            className="inline-flex items-center gap-2 py-1.5 px-5 rounded-full text-xs font-bold mb-5 uppercase tracking-[0.2em]"
            style={{
              background: 'rgba(60,179,113,0.1)',
              border: '1px solid rgba(60,179,113,0.25)',
              color: '#2d8653',
            }}
          >
            Capa de IA integrada
          </span>
          <h2 className="font-black text-slate-900 mb-4 font-display" style={{ fontSize: 'clamp(2rem, 5vw, 3.8rem)', letterSpacing: '-0.02em', lineHeight: 1.08 }}>
            Mucho más que una recepcionista IA
          </h2>
          <p className="text-slate-500 font-light leading-relaxed text-lg md:text-xl">
            Las recepcionistas IA responden. Ancora conecta atención, ventas, marketing e integraciones con tu operativa real.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-10">
          {PILLARS.map((pillar, index) => {
            const Icon = pillar.icon;
            return (
              <motion.div
                key={pillar.title}
                initial={{ opacity: 0, y: 22 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.07 }}
                className="rounded-3xl p-7 bg-white border border-slate-100 shadow-sm hover:-translate-y-1 hover:shadow-xl transition-all duration-300"
              >
                <div
                  className="w-14 h-14 rounded-2xl flex items-center justify-center mb-6"
                  style={{ background: `${pillar.accent}18`, border: `1px solid ${pillar.accent}35` }}
                >
                  <Icon size={27} style={{ color: pillar.accent }} />
                </div>
                <h3 className="text-xl font-black text-slate-900 mb-3 leading-tight">{pillar.title}</h3>
                <p className="text-sm text-slate-500 leading-relaxed font-light">{pillar.description}</p>
              </motion.div>
            );
          })}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="rounded-3xl p-6 md:p-8 text-center max-w-4xl mx-auto"
          style={{ background: 'linear-gradient(135deg, #0f172a, #1e293b)', border: '1px solid rgba(255,255,255,0.08)' }}
        >
          <p className="text-white text-xl md:text-2xl font-black mb-2">No sustituimos tu software. Lo conectamos.</p>
          <p className="text-slate-400 font-light">
            Seguís usando vuestra agenda, CRM o sistema de reservas. Ancora añade la capa inteligente para atención, seguimiento, ventas y marketing.
          </p>
        </motion.div>
      </div>
    </section>
  );
};
